/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Events } from './Events';
import { MousePosUtils } from './MousePosUtils';
import { Raycaster } from 'three';
import { ShowUiState, UiLog } from '../utils/DebugUI';
const DEBUG_STATE_HIDE_DELAY_MS = 300;
/**
 * enable object touching / editing etc. via raycasting
 */
export class ObjectSelector {
    /**
     * constructor
     * @param canvas the canvas that shows the 3d scene
     * @param eventWatcher watcher to detect number of listeners (for optimizations)
     */
    constructor(controls, canvas, eventWatcher) {
        this._rayCaster = new Raycaster();
        this._oldTouchIntersection = undefined;
        this._oldClickIntersection = undefined;
        this._enableDnD = false;
        this._isDrag = false;
        this._debugUiStates = null;
        this._debugUiLog = false;
        this._eventWatcher = eventWatcher;
        this._canvas = canvas;
        this._controls = controls;
        this._registerEvents();
        this._rayCaster.linePrecision = 0.02; // factor for eliminating z-dependent line width
        this._rayCaster.params.Points.threshold = 0.1;
        // this._rayCaster.precision = 0.02;
        if (ObjectSelector.showDebugUI) {
            if (ObjectSelector.showDebugUI.showStates) {
                this._debugUiStates = {
                    drag: new ShowUiState(Events.OBJECTDRAGGED),
                    objectTouched: new ShowUiState(Events.OBJECTTOUCHED),
                    objectClicked: new ShowUiState(Events.OBJECTCLICKED),
                    objectUnclicked: new ShowUiState(Events.OBJECTUNCLICKED),
                    objectReleased: new ShowUiState(Events.OBJECTRELEASED),
                };
            }
            if (ObjectSelector.showDebugUI.showConsole) {
                this._debugUiLog = true;
                UiLog.log('new ObjectSelector');
            }
        }
    }
    /**
     * enables drag and drop
     * @param on if true Dnd is enabled
     */
    enableDragAndDrop(on) {
        this._enableDnD = on;
    }
    /**
     * sets the current camera
     * @param camera the camera
     */
    setCurrentCamera(camera) {
        this._currentCamera = camera;
    }
    /**
     * sets the current scene
     * @param scene the scene
     */
    setCurrentScene(scene) {
        this._currentScene = scene;
    }
    _enableDragging() {
        if (this._enableDnD) {
            if (this._debugUiLog) {
                UiLog.log('enable dragging');
            }
            if (this._debugUiStates) {
                this._debugUiStates.drag.show();
            }
            this._isDrag = true;
            this._controls.pauseNavigator(true);
        }
    }
    _disableDragging() {
        if (this._enableDnD && this._isDrag) {
            if (this._debugUiLog) {
                UiLog.log('disable dragging');
            }
            if (this._debugUiStates) {
                this._debugUiStates.drag.hide();
            }
            this._isDrag = false;
            this._controls.pauseNavigator(false);
        }
    }
    _registerEvents() {
        const register = (event, fn) => this._canvas.addEventListener(event, fn, { passive: true });
        register('mousedown', (event) => {
            if (this._debugUiLog) {
                UiLog.log('mousedown');
            }
            this._onPointerDown(this._getMousePos(event), event.button);
        });
        register('mousemove', (event) => {
            this._onPointerMove(this._getMousePos(event), event.button);
        });
        register('mouseup', (event) => {
            if (this._debugUiLog) {
                UiLog.log('mouseup');
            }
            this._onPointerUp(this._getMousePos(event), event.button);
        });
        register('touchstart', (event) => {
            if (this._debugUiLog) {
                UiLog.log('touchstart');
            }
            this._onPointerDown(this._getMousePos(event.touches[0]), ObjectSelector.EVENT_BUTTON_TOUCH);
        });
        register('touchmove', (event) => {
            this._onPointerMove(this._getMousePos(event.changedTouches[0]), ObjectSelector.EVENT_BUTTON_TOUCH);
        });
        register('touchend', (event) => {
            if (this._debugUiLog) {
                UiLog.log('touchend');
            }
            this._onPointerUp(this._getMousePos(event.changedTouches[0]), ObjectSelector.EVENT_BUTTON_TOUCH);
        });
    }
    _onPointerDown(mousePos, button) {
        const { intersections, isUnclick } = this._getIntersectionAt(mousePos);
        if ( /* click */intersections.length) {
            this._enableDragging();
            this._dispatchEvent(Events.OBJECTCLICKED, () => {
                return {
                    button,
                    relPosition: mousePos,
                    new: intersections[0],
                    old: this._oldClickIntersection,
                    all: intersections,
                };
            });
            this._oldClickIntersection = intersections[0];
            if (this._debugUiStates) {
                this._debugUiStates.objectClicked.show(DEBUG_STATE_HIDE_DELAY_MS);
            }
        }
        else if (isUnclick) {
            this._dispatchEvent(Events.OBJECTUNCLICKED, () => {
                return {
                    button,
                    relPosition: mousePos,
                    old: this._oldClickIntersection,
                };
            });
            if (this._debugUiStates) {
                this._debugUiStates.objectUnclicked.show(DEBUG_STATE_HIDE_DELAY_MS);
            }
        }
    }
    _onPointerMove(mousePos, button) {
        if (this._isDrag) {
            this._dispatchEvent(Events.OBJECTDRAGGED, () => {
                return {
                    button,
                    relPosition: mousePos,
                    object: this._oldClickIntersection,
                };
            });
        }
        else {
            if (this._debugUiStates) {
                const intersection = this._getFirstIntersection(mousePos);
                if (intersection || this._oldTouchIntersection) {
                    this._debugUiStates.objectTouched.show(50);
                }
            }
            this._dispatchEvent(Events.OBJECTTOUCHED, () => {
                const intersection = this._getFirstIntersection(mousePos);
                const detail = {
                    new: intersection,
                    old: this._oldTouchIntersection,
                };
                this._oldTouchIntersection = intersection;
                return detail;
            });
        }
    }
    _onPointerUp(mousePos, button) {
        this._disableDragging();
        if (this._oldClickIntersection) {
            this._dispatchEvent(Events.OBJECTRELEASED, () => {
                return {
                    button,
                    relPosition: mousePos,
                    object: this._oldClickIntersection,
                };
            });
            // this._oldClickIntersection = null;
            if (this._debugUiStates) {
                this._debugUiStates.objectReleased.show(DEBUG_STATE_HIDE_DELAY_MS);
            }
        }
    }
    _getMousePos(event) {
        return MousePosUtils.readRelMousePos(event, this._canvas);
    }
    _getFirstIntersection(mousePos) {
        return this._getIntersectionAt(mousePos).intersections[0];
    }
    _getIntersectionAt({ mousex: x, mousey: y }) {
        const res = [];
        if (this._currentCamera && this._currentScene) {
            this._rayCaster.setFromCamera({ x, y }, this._currentCamera);
            const intersects = this._rayCaster.intersectObjects(this._currentScene.children, true);
            if (intersects) {
                for (const inter of intersects) {
                    const { userData } = inter.object;
                    // "imaginary" objects are completely ignored
                    if (userData[ObjectSelector.IMAGINARY]) {
                        continue;
                    }
                    // resort intersections by first:"interactionPriority" and as second by distance
                    const prio = ObjectSelector.getInteractionPriority(inter);
                    if (prio === 0) {
                        res.push(inter);
                    }
                    else {
                        let idx = 0;
                        for (; idx < res.length; idx++) {
                            if (prio > ObjectSelector.getInteractionPriority(res[idx])) {
                                break;
                            }
                        }
                        res.splice(idx, 0, inter);
                    }
                }
                // "untouchable" objects are also ignored but at the same time hide objects behind them
                for (let i = 0; i < res.length; i++) {
                    if (res[i].object.userData[ObjectSelector.UNTOUCHABLE]) {
                        return {
                            intersections: res.slice(0, i),
                            isUnclick: false,
                        };
                    }
                }
            }
        }
        return {
            intersections: res,
            isUnclick: res.length === 0,
        };
    }
    _dispatchEvent(event, getDetail) {
        if (this._debugUiLog) {
            UiLog.log(event);
        }
        if (this._currentScene && this._eventWatcher.hasListeners(event)) {
            this._currentScene.getStage().dispatchEvent(event, { detail: getDetail() });
        }
    }
}
ObjectSelector.IMAGINARY = 'imaginary';
ObjectSelector.UNTOUCHABLE = 'untouchable';
ObjectSelector.INTERACTION_PRIORITY = 'eventPriority';
ObjectSelector.EVENT_BUTTON_TOUCH = 1;
ObjectSelector.showDebugUI = null;
ObjectSelector.getInteractionPriority = (inter) => inter.object.userData[ObjectSelector.INTERACTION_PRIORITY] || 0;
//# sourceMappingURL=ObjectSelector.js.map